import { FileWarning } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ColumnMappingTable } from './column-mapping-table';
import { DataPreviewTable } from './data-preview-table';
import { ErrorsTable } from './errors-table';
import type { PreviewStageProps } from './types';

export function PreviewStage({
  isSuccess,
  validationResult,
  previewRows,
  columnHeaders,
}: PreviewStageProps) {
  return (
    <>
      {/* Validation Summary */}
      <div className="flex items-center gap-4 py-3 px-4 bg-muted/50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Total Rows:</span>
          <Badge variant="secondary">{validationResult?.total_rows || 0}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Valid:</span>
          <Badge variant="default" className="bg-emerald-700 text-white">
            {validationResult?.valid_rows || 0}
          </Badge>
        </div>
        {(validationResult?.invalid_rows || 0) > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Invalid:</span>
            <Badge variant="destructive">{validationResult?.invalid_rows}</Badge>
          </div>
        )}
      </div>

      {/* Schema Errors */}
      {validationResult?.schema_errors && validationResult.schema_errors.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start gap-2">
            <FileWarning className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Schema Errors</p>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {validationResult.schema_errors.map((err: string) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for Preview and Column Mapping */}
      <Tabs defaultValue="preview" className="flex-1 overflow-hidden flex flex-col">
        <TabsList>
          <TabsTrigger value="preview">Data Preview</TabsTrigger>
          <TabsTrigger value="mapping">Column Mapping</TabsTrigger>
          {!isSuccess && (
            <TabsTrigger value="errors">
              Errors ({validationResult?.field_errors?.length || 0})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Data Preview Tab */}
        <TabsContent value="preview" className="flex-1 overflow-hidden">
          <DataPreviewTable
            previewRows={previewRows}
            columnHeaders={columnHeaders}
            totalRows={validationResult?.total_rows || 0}
          />
        </TabsContent>

        {/* Column Mapping Tab */}
        <TabsContent value="mapping" className="flex-1 overflow-hidden">
          <ColumnMappingTable columnMappings={validationResult?.column_mappings || []} />
        </TabsContent>

        {/* Errors Tab */}
        {!isSuccess && (
          <TabsContent value="errors" className="flex-1 overflow-hidden">
            <ErrorsTable fieldErrors={validationResult?.field_errors || []} />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
}
